<template>
  <div>
    <v-layout class="grey lighten-2 pa-2" align-content-space-between align-center row>
      <v-flex class="subtle mr-2" shrink>
        <v-icon>assignment_turned_in</v-icon>
      </v-flex>
      <v-flex>
        <h1 class="all-caps subtle">Scheduled Service</h1>
      </v-flex>
    </v-layout>

    <v-layout row my-5>
      <v-flex grow mr-5>
        <ShiftHeader :shift="shift" />
      </v-flex>

      <v-flex shrink mx-5>
        <div v-if="shift.details" class="subtle">
          <p class="body-1"><span class="font-weight-bold text-uppercase">Shift Details: </span>{{ shift.details }}</p>
        </div>
        <div class="subtle">
          <p class="body-1 font-weight-bold">
            <BonusList :shift="shift" :role="user.role" />
          </p>
        </div>
        <v-layout row justify-left>
          <BonusView :shift="shift" type="travel" :role="user.role" />
          <BonusView :shift="shift" type="callIn" :role="user.role" />
          <BonusView :shift="shift" type="straight" :role="user.role" />
        </v-layout>
      </v-flex>

      <v-flex shrink ml-5>
        <div v-if="user.role == 'employee'"> <!-- employee pick up / trade / location -->

          <div v-if="!shiftHasPassed">
            <PickUpButton :shift="shift" :user="user" />
          </div>

          <v-dialog v-if="shift.locationMapEmbed" full-width v-model="mapDialog">
            <v-btn block slot='activator' class="white--text" color="grey darken-2">
              <v-icon left>
                location_on
              </v-icon>
              Location
            </v-btn>
            <v-card class="text-xs-center">
              <v-card-text>
                <div v-html="shift.locationMapEmbed"></div>
              </v-card-text>
            </v-card>
          </v-dialog>
        </div>
      </v-flex>

    </v-layout>

    <v-layout row>
      <!-- small bar -->
      <v-toolbar color="primary" flat dense class="hidden-md-and-up">
        <v-menu>
          <template v-slot:activator="{ on }">
            <v-toolbar-title v-on="on">
              <span class='white--text'>
                {{ componentNameReadable }}
                <v-icon color="white">arrow_drop_down</v-icon>
              </span>
            </v-toolbar-title>
          </template>
          <v-list>
            <v-list-tile
              v-for="item in tabOptions" :key="`${ item.routeName }-1`"
              :to="{ name: item.routeName }"
              v-if="showNavOptions(item)"
              :disabled="navDisabled(item)"
            >
              <v-list-tile-title v-text="item.linkLabel"></v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
      </v-toolbar>

      <!-- large bar -->
      <v-toolbar color="primary" flat dense class="hidden-sm-and-down"  style="overflow-x: auto;">
        <v-toolbar-items v-for="item in tabOptions" :key="`${ item.routeName }-1`" v-if="showNavOptions(item)">
          <v-btn
            small
            flat
            color="white"
            :to="{ name: item.routeName }"
            exact
            :disabled="navDisabled(item)"
          >
            {{ item.linkLabel }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
    </v-layout>

    <v-layout row>
      <component
        :is="componentName"
        v-bind="{ shift, user, client: true }"
      ></component>
    </v-layout>

  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ApiField from '@/components/base/api-field.vue'
import PickUpButton from '@/components/calendar/pick-up-button.vue'
import ShiftHeader from '@/components/calendar/shift-header.vue'
import ShiftContact from '@/components/calendar/shift-contact.vue'
import SARNotes from '@/components/calendar/sar-notes.vue'

import Detail from '@/components/calendar/employee-tabs/detail.vue'
import History from '@/components/calendar/employee-tabs/history.vue'
import Trade from '@/components/calendar/employee-tabs/trade.vue'

export default {
  props: [
    'user',
    'shift'
  ],
  components: {
    Detail,
    History,
    Trade,

    ApiField,
    PickUpButton,
    ShiftHeader,
    ShiftContact,
    SARNotes,
  },
  created () {
    this.setUp()
  },
  watch: {
  },
  computed: {
    shiftHasPassed: function() {
      return this.$moment(this.shift.startDatetime).isBefore()
    },
    SARActive: function() {
      return (
        this.shift.userId == this.user.id &&
        this.shiftHasPassed
      )
    },
    componentName() {
      const currentComponent = this.tabOptions.find((o) => {
        return o.routeName === this.$router.currentRoute.name
      })

      if (!currentComponent) {
        this.$router.push({ name: 'shift/detail' })
        return 'Detail'
      }

      return currentComponent.componentName
    },
    componentNameReadable() {
      const currentComponent = this.tabOptions.find((o) => {
        return o.routeName === this.$router.currentRoute.name
      })

      if (!currentComponent) {
        this.$router.push({ name: 'shift/detail' })
        return 'Detail'
      }

      return currentComponent.linkLabel
    }
  },
  data: function () {
    return {
      shiftId: null,
      mapDialog: false,
      tabOptions: [
        {
          routeName: "shift/detail",
          linkLabel: "Detail",
          componentName: 'Detail'
        },
        {
          routeName: "shift/work-history",
          linkLabel: "Work History",
          componentName: 'History',
        },
        {
          routeName: "shift/contact",
          linkLabel: "Contact",
          componentName: 'ShiftContact',
        },
        {
          routeName: "shift/trade-request",
          linkLabel: "Trade Request",
          componentName: 'Trade',
        },
        {
          routeName: "shift/sar",
          linkLabel: "SAR",
          componentName: 'SARNotes',
        },
      ]
    }
  },
  methods: {
    setUp () {
      this.$store.dispatch('timeclock/getPunches', { shiftId: this.shift.id })
    },

    navDisabled(item) {
      switch(item.componentName) {
        case 'ShiftReview':
          return !this.reviewable
        case 'Trade':
          return this.shiftHasPassed
        default:
          return false
      }
    },

    showNavOptions(item) {
      switch(item.componentName) {
        case 'Trade':
          // only show this for the user who owns the shift OR the user who accepted
          // the trade
          if (this.user.id == this.shift.userId || this.user.id == this.shift.tradeAcceptUserId) {
            return true
          } else {
            return false
          }
        case 'SARNotes':
          if (this.SARActive) {
            return true
          } else {
            return false
          }
        default:
          return true
      }
    },
  }
}
</script>
